import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { TravelDocument } from '../../common/TravelDocument';
import { staggeredBaseQuery } from './staggeredBaseQuery'; 

export const travelDocumentApi = createApi({
  reducerPath: 'travelDocumentApi',
  tagTypes: ['TravelDocument', 'ProfileTravelDocument'],
  baseQuery: staggeredBaseQuery(''),
  endpoints: (builder) => ({
    getTravelDocuments: builder.query<TravelDocument[], String>({
      query: (jobOrderId) => {
        return {
          method: 'GET',
          url: `jobOrders/${jobOrderId}/travelDocuments`,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'TravelDocument' as const, id })),
              { type: 'TravelDocument', id: 'LIST' },
            ]
          : [{ type: 'TravelDocument', id: 'LIST' }],
    }),
    getTrainingTravelDocuments: builder.query<TravelDocument[], String>({
      query: (trainingOrderId) => {
        return {
          method: `GET`,
          url: `trainingOrders/${trainingOrderId}/travelDocuments`,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'TravelDocument' as const, id })),
              { type: 'TravelDocument', id: 'LIST' },
            ]
          : [{ type: 'TravelDocument', id: 'LIST' }],
    }),
    getTravelDocumentsByProfile: builder.query<TravelDocument[], {jobOrderId: number, jobOrderIds: number[]}>({
      query: ({jobOrderId, jobOrderIds}) => {
        return {
          method: 'GET',
          url: `jobOrders/${jobOrderId}/travelDocuments/profile?jobOrderIds=${jobOrderIds}`,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'ProfileTravelDocument' as const, id })),
              { type: 'ProfileTravelDocument', id: 'LIST' },
            ]
          : [{ type: 'ProfileTravelDocument', id: 'LIST' }],
    }),
    getTravelDocument: builder.query<TravelDocument, { jobOrderId: String, travelDocumentId: String }>({
      query: ({jobOrderId, travelDocumentId}) => {
        return {
          method: 'GET',
          url: `jobOrders/${jobOrderId}/travelDocuments/${travelDocumentId}`,
        };
      },
    }),
    getTrainingTravelDocument: builder.query<TravelDocument, { trainingOrderId: String, travelDocumentId: String }>({
      query: ({trainingOrderId, travelDocumentId}) => {
        return {
          method: 'GET',
          url: `trainingOrders/${trainingOrderId}/travelDocuments/${travelDocumentId}`,
        };
      },
    }),
    downloadTravelDocument: builder.mutation<Blob, {jobOrderId: String, fileKey: String }>({
      query: ({jobOrderId, fileKey}) => {
        return {
          method: 'GET',
          url: `jobOrders/${jobOrderId}${fileKey.charAt(0) === '/' ? fileKey : `/${fileKey}`}`,
          responseHandler: (response) => response.blob()
        };
      },
    }),
    downloadTrainingTravelDocument: builder.mutation<Blob, {trainingOrderId: String, fileKey: String }>({
      query: ({trainingOrderId, fileKey}) => {
        return {
          method: 'GET',
          url: `trainingOrders/${trainingOrderId}/travelDocuments/download${fileKey.charAt(0) === '/' ? fileKey : `/${fileKey}`}`,
          responseHandler: (response) => response.blob()
        };
      },
    }),
    uploadTravelDocument: builder.mutation<TravelDocument, { jobOrderId: String, data: FormData }>({
      query: ({jobOrderId, data}) => {
        return {
          method: 'POST',
          url: `jobOrders/${jobOrderId}/travelDocuments/`,
          body: data,
        };
      },
      invalidatesTags: [{ type: 'TravelDocument', id: 'LIST' }],
    }),
    uploadTrainingTravelDocument: builder.mutation<TravelDocument, { trainingOrderId: String, data: FormData }>({
      query: ({trainingOrderId, data}) => {
        return {
          method: 'POST',
          url: `trainingOrders/${trainingOrderId}/travelDocuments/`,
          body: data,
        };
      },
      invalidatesTags: [{ type: 'TravelDocument', id: 'LIST' }],
    }),
    deleteTravelDocument: builder.mutation<void, { jobOrderId: String, travelDocumentId: String }>({
      query: ({jobOrderId, travelDocumentId}) => {
        return {
          method: 'DELETE',
          url: `jobOrders/${jobOrderId}/travelDocuments/${travelDocumentId}`,
        };
      },
      invalidatesTags: (result, error, travelDocumentId) => [{ type: 'TravelDocument', travelDocumentId }],
    }),
    deleteTrainingTravelDocument: builder.mutation<void, { trainingOrderId: String, travelDocumentId: String }>({
      query: ({trainingOrderId, travelDocumentId}) => {
        return {
          method: 'DELETE',
          url: `trainingOrders/${trainingOrderId}/travelDocuments/${travelDocumentId}`,
        };
      },
      invalidatesTags: (result, error, travelDocumentId) => [{ type: 'TravelDocument', travelDocumentId }],
    }),
  }),
});

export const {
  useGetTravelDocumentsQuery,
  useGetTrainingTravelDocumentsQuery,
  useGetTravelDocumentsByProfileQuery,
  useGetTravelDocumentQuery,
  useGetTrainingTravelDocumentQuery,
  useDownloadTravelDocumentMutation,
  useDownloadTrainingTravelDocumentMutation,
  useUploadTravelDocumentMutation,
  useUploadTrainingTravelDocumentMutation,
  useDeleteTravelDocumentMutation,
  useDeleteTrainingTravelDocumentMutation,
} = travelDocumentApi;